import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
  LayoutChangeEvent,
  LayoutAnimation,
} from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import {
  MaintenanceCTACard,
  MaintenanceHeroCard,
  MaintenanceRequestRow,
  MaintenanceEmptyState,
  PageHeader,
} from '@/components/residents/redesign';
import { AnimatedEntrance } from '@/components/shared/redesign';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { pickHeroTicket } from '@/utils/residents/maintenance';
import { DesktopMaintenanceListViewSkeleton } from './DesktopMaintenanceListViewSkeleton';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

const CONTENT_PADDING = REDESIGN_SPACING.horizontalPadding;
// Match the payments/events desktop content frame.
const MAX_CONTENT_WIDTH = 1100;
const CARD_GAP = 24;
const MIN_CARD_WIDTH = 280;

interface DesktopMaintenanceListViewProps {
  openTickets: IMaintenanceListResponse[];
  closedTickets: IMaintenanceListResponse[];
  loading: boolean;
  error: string | null;
  onNewRequest: () => void;
  onTicketPress: (ticket: IMaintenanceListResponse) => void;
}

export const DesktopMaintenanceListView: React.FC<DesktopMaintenanceListViewProps> = ({
  openTickets,
  closedTickets,
  loading,
  error,
  onNewRequest,
  onTicketPress,
}) => {
  const { colors, semantic, components } = useThemeColors();
  const [containerWidth, setContainerWidth] = useState(0);
  const [closedExpanded, setClosedExpanded] = useState(false);
  const reduceMotion = useReducedMotion();

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const toggleClosed = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setClosedExpanded((v) => !v);
  }, []);

  // The content frame is capped and centered, so the grid math clamps too.
  const innerWidth = Math.min(containerWidth, MAX_CONTENT_WIDTH) - CONTENT_PADDING * 2;
  const numColumns = innerWidth >= MIN_CARD_WIDTH * 3 + CARD_GAP * 2 ? 3 : 2;
  const cardWidth = containerWidth > 0
    ? Math.floor((innerWidth - CARD_GAP * (numColumns - 1)) / numColumns)
    : 0;

  const hasAny = openTickets.length + closedTickets.length > 0;
  const hero = pickHeroTicket(openTickets);
  const otherOpen = hero ? openTickets.filter((t) => t !== hero) : openTickets;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      onLayout={handleLayout}
    >
      <PageHeader
        title="Maintenance"
        rightAction={
          <Hoverable
            hover="button"
            style={[styles.newButton, { backgroundColor: semantic.interactive.primary }]}
            onPress={onNewRequest}
            accessibilityRole="button"
          >
            <Text style={[styles.newButtonText, { color: components.button.primaryText }]}>New request</Text>
          </Hoverable>
        }
      />

      {loading && <DesktopMaintenanceListViewSkeleton />}

      {error && !loading && (
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: colors.accent.red }]}>{error}</Text>
        </View>
      )}

      {!loading && !error && !hasAny && (
        <>
          <MaintenanceCTACard onPress={onNewRequest} variant="expanded" />
          <MaintenanceEmptyState />
        </>
      )}

      {!loading && !error && hasAny && (
        <>
          {hero ? (
            <AnimatedEntrance index={0} disabled={reduceMotion} style={styles.heroWrap}>
              <MaintenanceHeroCard ticket={hero} onPress={() => onTicketPress(hero)} />
            </AnimatedEntrance>
          ) : (
            <MaintenanceEmptyState
              title="No open requests"
              subtitle="You have no open service requests at the moment"
            />
          )}

          {otherOpen.length > 0 && (
            <>
              <AnimatedEntrance index={1} disabled={reduceMotion}>
                <Text style={[styles.sectionHeader, { color: colors.text.muted }]}>More open</Text>
              </AnimatedEntrance>
              <View style={styles.cardsGrid}>
                {otherOpen.map((ticket, i) => (
                  <AnimatedEntrance
                    key={ticket.maintenanceRequestId}
                    index={2 + i}
                    disabled={reduceMotion}
                    style={{ width: cardWidth }}
                  >
                    <MaintenanceRequestRow
                      ticket={ticket}
                      onPress={() => onTicketPress(ticket)}
                      width={cardWidth}
                    />
                  </AnimatedEntrance>
                ))}
              </View>
            </>
          )}

          {closedTickets.length > 0 && (
            <>
              <Hoverable hover="link" onPress={toggleClosed} accessibilityRole="button" style={styles.closedLink}>
                {({ textStyle }) => (
                  <Text style={[styles.closedLinkText, { color: semantic.interactive.primary }, textStyle]}>
                    {closedExpanded
                      ? 'Hide closed requests'
                      : `View ${closedTickets.length} closed request${closedTickets.length === 1 ? '' : 's'}`}
                  </Text>
                )}
              </Hoverable>
              {closedExpanded && (
                <View style={styles.cardsGrid}>
                  {closedTickets.map((ticket) => (
                    <MaintenanceRequestRow
                      key={ticket.maintenanceRequestId}
                      ticket={ticket}
                      dimmed
                      onPress={() => onTicketPress(ticket)}
                      width={cardWidth}
                    />
                  ))}
                </View>
              )}
            </>
          )}
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  contentContainer: {
    padding: CONTENT_PADDING,
    paddingBottom: 60,
    gap: 24,
    flexGrow: 1,
    maxWidth: MAX_CONTENT_WIDTH,
    width: '100%',
    alignSelf: 'center',
  },
  newButton: {
    borderRadius: 8,
    height: 36,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  newButtonText: { ...REDESIGN_TYPOGRAPHY.captionMedium, lineHeight: 18 },
  heroWrap: { width: '100%' },
  sectionHeader: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: -8,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: CARD_GAP,
    ...(Platform.OS === 'web' && ({ display: 'flex' } as any)),
  },
  closedLink: { alignSelf: 'flex-start', paddingVertical: 4 },
  closedLinkText: { ...REDESIGN_TYPOGRAPHY.body, fontWeight: '500' },
  errorContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: 40 },
  errorText: { ...REDESIGN_TYPOGRAPHY.body, textAlign: 'center' },
});

export default DesktopMaintenanceListView;
